import React, { useState, useEffect, useRef } from "react";
import * as styles from "../styles/menu-bar.module.scss";
import IconArrowLeft from "./icons/IconArrowLeft";
import IconArrowRight from "./icons/IconArrowRight";
import IconArrowUp from "./icons/IconArrowUp";
import RichText from "./RichText";

const MenuBar = ({ sticky = true, items = [], center = false }) => {
	const [isSticky, setIsSticky] = useState(false);
	const [activeItem, setActiveItem] = useState(null);
	const [showLeftArrow, setShowLeftArrow] = useState(false);
	const [showRightArrow, setShowRightArrow] = useState(false);
	const containerRef = useRef(null);
	const stickyItemsRef = useRef(null);
	useEffect(() => {
		if (!sticky) return;

		const handleScroll = () => {
			if (!containerRef.current) return;

			const rect = containerRef.current.getBoundingClientRect();
			const bottom = rect.bottom;
			const windowHeight = window.innerHeight;
			const delta =
				window.scrollY - (containerRef.current.dataset.lastScroll || 0);

			// Sticky Logic
			let shouldBeSticky = bottom < 0;

			if (shouldBeSticky) {
				const allMenus = Array.from(document.querySelectorAll(".js-menu-bar"));
				const myIndex = allMenus.indexOf(containerRef.current);
				const nextMenu = allMenus[myIndex + 1];

				if (nextMenu) {
					const nextRect = nextMenu.getBoundingClientRect();
					if (nextRect.top <= 0) {
						shouldBeSticky = false;
					}
				}
			}

			setIsSticky(shouldBeSticky);

			// Scrollspy Logic
			if (bottom < 0) {
				let currentActive = null;
				for (let i = items.length - 1; i >= 0; i--) {
					const item = items[i];
					if (!item.linkId || !item.linkId.startsWith("#")) continue;
					const id = item.linkId.substring(1); // remove '#'
					const element = document.getElementById(id);
					if (element) {
						const elRect = element.getBoundingClientRect();
						if (elRect.top <= 150) {
							currentActive = item.id;
							break;
						}
					}
				}
				if (currentActive) {
					setActiveItem(currentActive);
				}
			}
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, [sticky, items]);

	// Check scroll arrows visibility
	useEffect(() => {
		if (isSticky && stickyItemsRef.current) {
			checkScrollArrows();
			// Also check on resize
			window.addEventListener("resize", checkScrollArrows);
			return () => window.removeEventListener("resize", checkScrollArrows);
		}
	}, [isSticky]);

	const checkScrollArrows = () => {
		if (stickyItemsRef.current) {
			const { scrollLeft, scrollWidth, clientWidth } = stickyItemsRef.current;
			setShowLeftArrow(scrollLeft > 1);
			setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
		}
	};

	const scrollStickyItems = (direction) => {
		if (stickyItemsRef.current) {
			const scrollAmount = 200;
			stickyItemsRef.current.scrollBy({
				left: direction === "left" ? -scrollAmount : scrollAmount,
				behavior: "smooth",
			});
			setTimeout(checkScrollArrows, 300);
		}
	};

	const handleReturn = () => {
		setIsSticky(false);
		if (containerRef.current) {
			const headerOffset = window.innerWidth <= 768 ? 100 : 150;
			const top =
				containerRef.current.getBoundingClientRect().top +
				window.pageYOffset -
				headerOffset;
			window.scrollTo({ top, behavior: "smooth" });
		}
	};

	const handleItemClick = (e, linkId) => {
		if (linkId) {
			if (linkId.startsWith("#")) {
				e.preventDefault();
				const id = linkId.substring(1);
				const element = document.getElementById(id);
				if (element) {
					const nav = document.querySelector("header");
					const headerOffset = (nav ? nav.offsetHeight : 80) + 16;
					const elementPosition = element.getBoundingClientRect().top;
					const offsetPosition =
						elementPosition + window.pageYOffset - headerOffset;

					window.scrollTo({
						top: offsetPosition,
						behavior: "smooth",
					});
				}
			} else {
				// It's an external link, let default behavior happen or force it if needed
				// If it's just an anchor tag wrapping, we don't need to do anything here.
				// But if we are calling this from a div onClick, we need to navigate.
				window.open(linkId, "_blank", "noopener,noreferrer");
			}
		}
	};

	// Render Card Content helper (since we used it in the loop)
	const renderCard = (item) => (
		<div
			key={item.id}
			className={styles.card}
			onClick={(e) => handleItemClick(e, item.linkId)}
		>
			<div className={styles.cardContent}>
				<span className={styles.icon}>{item.icon}</span>
				<span className={styles.title}>{item.title}</span>
				<span className={styles.cta}>{item.cta}</span>
			</div>
			<div className={styles.revealContent}>
				<p>
					<RichText description={item.description} />
				</p>
			</div>
		</div>
	);

	return (
		<>
			<div
				ref={containerRef}
				className={`${styles.container} js-menu-bar${center ? " " + styles.center : ""}`}
				style={{ "--menu-cols": Math.min(items.length, 3) }}
			>
				<div
					className={`${styles.menuGrid}${center ? " " + styles.center : ""}`}
				>
					{items.map((item) => renderCard(item))}
				</div>
			</div>

			{/* Sticky Header */}
			{sticky && (
				<div
					className={`${styles.stickyHeader} ${isSticky ? styles.visible : ""}`}
				>
					<div className={styles.stickyContent}>
						<div className={styles.stickyItemsWrapper}>
							{showLeftArrow && (
								<button
									className={`${styles.scrollButton} ${styles.scrollButtonLeft}`}
									onClick={() => scrollStickyItems("left")}
								>
									<IconArrowLeft />
								</button>
							)}

							<div
								className={`${styles.stickyItems} ${center ? styles.center : styles.left}`}
								ref={stickyItemsRef}
								onScroll={checkScrollArrows}
							>
								{items.map((item) => (
									<div
										key={item.id}
										className={`${styles.stickyItem} ${activeItem === item.id ? styles.active : ""}`}
										onClick={(e) => handleItemClick(e, item.linkId)}
									>
										{item.title}
									</div>
								))}
							</div>

							{showRightArrow && (
								<button
									className={`${styles.scrollButton} ${styles.scrollButtonRight}`}
									onClick={() => scrollStickyItems("right")}
								>
									<IconArrowRight />
								</button>
							)}
						</div>

						<div
							className={styles.returnArrow}
							onClick={handleReturn}
							title="Return to menu"
						>
							<IconArrowUp />
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default MenuBar;
